import { useDealership } from '@/contexts/DealershipContext'
import { dataGeneratorApi } from '@/services/dataGenerator.api'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Grid,
  Group,
  NumberInput,
  Progress,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { IconCheck, IconDownload, IconPlus, IconRefresh, IconTrash, IconX } from '@tabler/icons-react'
import React, { useState } from 'react'

interface GenerationSettings {
  count: number
  dealerId: string
  overridePhone: string
  overrideEmail: string
  excludeEmail: boolean
  excludePhone: boolean
}

/** One-line label for progress / results: lead name + grounded VOI from desired_vehicle[0]. */
function formatLeadSummary(customer: Record<string, any> | null | undefined): string {
  if (!customer) return '—'
  const first = customer.person?.first_name ?? ''
  const last = customer.person?.last_name ?? ''
  const name = [first, last].filter(Boolean).join(' ').trim() || 'Unknown lead'
  const vehicles = customer.desired_vehicle
  if (!Array.isArray(vehicles) || vehicles.length === 0) {
    return `${name} — No VOI`
  }
  const v = vehicles[0] as Record<string, any>
  const ymm = [v.model_year, v.make, v.model_name]
    .filter(x => x != null && x !== '')
    .join(' ')
    .trim()
  const bits: string[] = []
  if (ymm) bits.push(ymm)
  if (v.stock_number) bits.push(`#${v.stock_number}`)
  if (v.vin) bits.push(`VIN …${String(v.vin).slice(-6)}`)
  const vehicleLabel = bits.length > 0 ? bits.join(' · ') : 'VOI (no YMM in payload)'
  return `${name} — ${vehicleLabel}`
}

export function DataGenerator() {
  const { entities } = useDealership()
  const [settings, setSettings] = useState<GenerationSettings>({
    count: 1,
    dealerId: '',
    overridePhone: '',
    overrideEmail: '',
    excludeEmail: false,
    excludePhone: false,
  })
  const [isGenerating, setIsGenerating] = useState(false)
  const [generationResults, setGenerationResults] = useState<any[]>([])
  const [showResults, setShowResults] = useState(false)
  const [isCleaningUp, setIsCleaningUp] = useState(false)
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 })
  const [latestLeadSummary, setLatestLeadSummary] = useState<string | null>(null)

  const [volumeSettings, setVolumeSettings] = useState({
    count: 10,
    intervalSeconds: 2,
    dealerId: '',
  })
  const [isVolumeRunning, setIsVolumeRunning] = useState(false)
  const [volumeBurstResult, setVolumeBurstResult] = useState<Awaited<
    ReturnType<typeof dataGeneratorApi.runPromaxVolumeEmailBurst>
  > | null>(null)
  const [showVolumeResults, setShowVolumeResults] = useState(false)

  // Get all dealerships from entities
  const getAllDealerships = () => {
    const dealerships: Array<{ value: string; label: string }> = []

    entities.forEach(entity => {
      if (entity.type === 'group') {
        const group = entity as any // DealershipGroup
        group.dealerships?.forEach((dealer: any) => {
          dealerships.push({
            value: dealer.id,
            label: `${dealer.name} (${group.name})`,
          })
        })
      } else if (entity.type === 'dealership') {
        const dealer = entity as any // Dealership
        dealerships.push({
          value: dealer.id,
          label: dealer.name,
        })
      }
    })

    return dealerships.sort((a, b) => a.label.localeCompare(b.label))
  }

  const handleGenerate = async () => {
    if (!settings.dealerId) {
      alert('Please select a dealership')
      return
    }

    setIsGenerating(true)

    // Calculate total expected records
    setGenerationProgress({ current: 0, total: settings.count })
    setLatestLeadSummary(null)

    // Show results panel immediately for real-time updates
    setGenerationResults([])
    setShowResults(true)

    try {
      const dealerIdNum = parseInt(settings.dealerId)

      for (let i = 0; i < settings.count; i++) {
        const { customer } = await dataGeneratorApi.getRandomLeadPayload(dealerIdNum)
        if (settings.excludeEmail) {
          const comms = customer.person?.communications ?? []
          customer.person = {
            ...customer.person,
            communications: comms.filter((c: any) => c.communication_type !== 'PrimaryEmail'),
          }
        }
        if (settings.excludePhone) {
          const comms = customer.person?.communications ?? []
          customer.person = {
            ...customer.person,
            communications: comms.filter(
              (c: any) => !['MobilePhone', 'HomePhone', 'WorkPhone', 'CellPhone'].includes(c.communication_type),
            ),
          }
        }
        if (settings.overrideEmail?.trim()) {
          const emailComm = customer.person?.communications?.find((c: any) => c.communication_type === 'PrimaryEmail')
          if (emailComm) emailComm.email_address = settings.overrideEmail.trim()
        }
        if (settings.overridePhone?.trim()) {
          customer.person?.communications?.forEach((c: any) => {
            if (['MobilePhone', 'HomePhone', 'WorkPhone', 'CellPhone'].includes(c.communication_type))
              c.complete_number = settings.overridePhone.trim()
          })
        }
        const result = await dataGeneratorApi.generateSingleCustomer(customer, dealerIdNum)
        const summary = formatLeadSummary(customer as Record<string, any>)
        setGenerationResults(prev => [
          ...prev,
          {
            id: `generated-${Date.now()}-${i}-${crypto.randomUUID()}`,
            template: summary,
            data: customer,
            status: result.success ? 'success' : 'error',
            customerId: result.customerId,
            error: result.error,
          },
        ])
        setLatestLeadSummary(summary)
        setGenerationProgress(prev => ({ ...prev, current: prev.current + 1 }))
      }

      // Generation complete - progress bar will show 100%
    } catch (error) {
      console.error('Generation failed:', error)
      // Add error to results so user can see what failed
      setGenerationResults(prev => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          template: 'Error',
          data: null,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      ])
    } finally {
      setIsGenerating(false)
    }
  }

  const handlePromaxVolumeBurst = async () => {
    if (!volumeSettings.dealerId) {
      alert('Please select a dealership for the volume simulator')
      return
    }

    setIsVolumeRunning(true)
    setVolumeBurstResult(null)
    setShowVolumeResults(true)

    try {
      const result = await dataGeneratorApi.runPromaxVolumeEmailBurst({
        count: volumeSettings.count,
        intervalMs: volumeSettings.intervalSeconds * 1000,
        dealerId: volumeSettings.dealerId,
      })
      setVolumeBurstResult(result)
    } catch (error) {
      console.error('Promax volume burst failed:', error)
      alert(error instanceof Error ? error.message : 'Promax volume burst failed')
      setShowVolumeResults(false)
    } finally {
      setIsVolumeRunning(false)
    }
  }

  const handleExportResults = () => {
    const dataStr = JSON.stringify(generationResults, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `generated-customers-${Date.now()}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleCleanupTestData = async () => {
    if (!confirm(`Are you sure you want to delete all test data? This action cannot be undone.`)) {
      return
    }

    setIsCleaningUp(true)
    try {
      const response = await dataGeneratorApi.cleanupTestData()
      if (response.success) {
        // Build a detailed message showing what was deleted
        if (response.details) {
          const { customers, conversations, messages } = response.details
          const breakdown = [
            `${customers} customer${customers !== 1 ? 's' : ''}`,
            `${conversations} conversation${conversations !== 1 ? 's' : ''}`,
            `${messages} message${messages !== 1 ? 's' : ''}`,
          ].filter(item => {
            // Only show items that had records deleted
            const count = parseInt(item.match(/^\d+/)?.[0] || '0')
            return count > 0
          })

          alert(`Successfully deleted:\n${breakdown.join('\n')}\n\nTotal: ${response.deletedCount} records`)
        } else {
          // Fallback if details aren't available
          alert(`Successfully deleted ${response.deletedCount} test records`)
        }
        setGenerationResults([])
        setShowResults(false)
      } else {
        alert('Cleanup failed')
      }
    } catch (error) {
      console.error('Cleanup failed:', error)
      alert('Cleanup failed: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setIsCleaningUp(false)
    }
  }

  return (
    <Stack gap="sm">
      <Card withBorder padding="md">
        <Stack gap="sm">
          <div>
            <Title order={4}>Random lead generation</Title>
            <Text size="xs" c="dimmed" mt={4}>
              Generate test leads: random names/contact; vehicle cycles through 4 patterns — no desired vehicle, then vehicle w/
              stock, then year/make/model, then make/model-only (repeat).
            </Text>
          </div>

          <Grid gutter="md">
            <Grid.Col span={{ base: 12, xs: 6, md: 2 }}>
              <NumberInput
                label="Leads"
                value={settings.count}
                onChange={(value: string | number) => setSettings(prev => ({ ...prev, count: Number(value) || 1 }))}
                min={1}
                max={100}
                size="sm"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
              <Select
                label="Dealership"
                placeholder="Select dealership"
                value={settings.dealerId}
                onChange={(value: string | null) => setSettings(prev => ({ ...prev, dealerId: value || '' }))}
                data={getAllDealerships()}
                searchable
                clearable
                size="sm"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, md: 2 }}>
              <TextInput
                label="Phone override"
                value={settings.overridePhone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSettings(prev => ({ ...prev, overridePhone: e.target.value }))
                }
                placeholder="+15551234567"
                size="sm"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, md: 2 }}>
              <TextInput
                label="Email override"
                value={settings.overrideEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSettings(prev => ({ ...prev, overrideEmail: e.target.value }))
                }
                placeholder="you@example.com"
                size="sm"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, md: 2 }} style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
              <Checkbox
                label="Exclude email"
                checked={settings.excludeEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSettings(prev => ({ ...prev, excludeEmail: e.target.checked }))
                }
                size="sm"
              />
              <Checkbox
                label="Exclude phone"
                checked={settings.excludePhone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSettings(prev => ({ ...prev, excludePhone: e.target.checked }))
                }
                size="sm"
              />
            </Grid.Col>
          </Grid>

          {isGenerating && generationProgress.total > 0 && (
            <Stack gap={4}>
              <Group gap="xs" wrap="nowrap">
                <Progress
                  value={(generationProgress.current / generationProgress.total) * 100}
                  size="sm"
                  style={{ flex: 1, minWidth: 120 }}
                  animated
                  color="blue"
                />
                <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {generationProgress.current}/{generationProgress.total}
                </Text>
              </Group>
              {latestLeadSummary && (
                <Text size="xs" c="dimmed" lineClamp={2} style={{ wordBreak: 'break-word' }}>
                  <Text span fw={500} c="dark.4">
                    Latest:
                  </Text>{' '}
                  {latestLeadSummary}
                </Text>
              )}
            </Stack>
          )}

          <Group gap="xs">
            <Button
              onClick={handleGenerate}
              loading={isGenerating}
              leftSection={<IconPlus size={16} />}
              disabled={!settings.dealerId}
              size="sm"
            >
              Generate leads
            </Button>
            <Button
              onClick={handleCleanupTestData}
              loading={isCleaningUp}
              leftSection={<IconTrash size={16} />}
              variant="light"
              color="red"
              size="sm"
            >
              Cleanup test data
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <div>
            <Title order={4}>DEX Message Data Generator</Title>
            <Text size="xs" c="dimmed" mt={4}>
              Sends ADF lead emails into Promax.
            </Text>
          </div>

          <Grid gutter="md">
            <Grid.Col span={{ base: 12, xs: 6, md: 2 }}>
              <NumberInput
                label="Leads"
                value={volumeSettings.count}
                onChange={(value: string | number) =>
                  setVolumeSettings(prev => ({ ...prev, count: Math.min(30, Math.max(1, Number(value) || 1)) }))
                }
                min={1}
                max={30}
                size="sm"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, md: 2 }}>
              <NumberInput
                label="Interval (seconds)"
                value={volumeSettings.intervalSeconds}
                onChange={(value: string | number) =>
                  setVolumeSettings(prev => ({
                    ...prev,
                    intervalSeconds: Math.min(60, Math.max(1, Number(value) || 1)),
                  }))
                }
                min={1}
                max={60}
                size="sm"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
              <Select
                label="Dealership"
                placeholder="Select dealership"
                value={volumeSettings.dealerId}
                onChange={(value: string | null) => setVolumeSettings(prev => ({ ...prev, dealerId: value || '' }))}
                data={getAllDealerships()}
                searchable
                clearable
                size="sm"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, md: 4 }} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Text size="xs" c="dimmed">
                Est. duration: ~{Math.max(0, volumeSettings.count - 1) * volumeSettings.intervalSeconds}s (max 30 leads, 1s min
                interval)
              </Text>
            </Grid.Col>
          </Grid>

          <Group gap="xs">
            <Button
              onClick={handlePromaxVolumeBurst}
              loading={isVolumeRunning}
              leftSection={<IconPlus size={16} />}
              disabled={!volumeSettings.dealerId}
              size="sm"
              variant="light"
            >
              Run volume burst
            </Button>
            {showVolumeResults && volumeBurstResult && (
              <Button
                variant="subtle"
                size="sm"
                onClick={() => {
                  setShowVolumeResults(false)
                  setVolumeBurstResult(null)
                }}
              >
                Clear burst results
              </Button>
            )}
          </Group>

          {isVolumeRunning && (
            <Text size="sm" c="dimmed">
              Sending leads via email to Promax… this may take up to ~
              {Math.max(0, volumeSettings.count - 1) * volumeSettings.intervalSeconds + 5}s.
            </Text>
          )}

          {showVolumeResults && volumeBurstResult && (
            <Stack gap="xs">
              <Group gap="xs">
                <Badge size="sm" variant="light">
                  burst {volumeBurstResult.burstId.slice(0, 8)}
                </Badge>
                <Badge size="sm" color="green" variant="light">
                  {volumeBurstResult.sent} sent
                </Badge>
                <Badge size="sm" color="red" variant="light">
                  {volumeBurstResult.failed} failed
                </Badge>
                <Text size="xs" c="dimmed">
                  {volumeBurstResult.durationMs}ms total
                </Text>
              </Group>
              <Stack gap={4} style={{ maxHeight: 240, overflowY: 'auto' }}>
                {volumeBurstResult.results.map(row => (
                  <Group key={row.index} justify="space-between" wrap="nowrap" gap="sm">
                    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Badge
                        size="sm"
                        color={row.success ? 'green' : 'red'}
                        leftSection={row.success ? <IconCheck size={10} /> : <IconX size={10} />}
                      >
                        {row.index}
                      </Badge>
                      <Text size="sm" truncate>
                        {row.customerName} — {row.customerEmail}
                      </Text>
                    </Group>
                    {row.error && (
                      <Text size="xs" c="red" lineClamp={1}>
                        {row.error}
                      </Text>
                    )}
                  </Group>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Card>

      {showResults && (
        <Card withBorder padding="md">
          <Group justify="space-between" align="flex-start" mb="xs">
            <Group gap="xs">
              <Title order={5} style={{ margin: 0 }}>
                Results
              </Title>
              {generationProgress.total > 0 && (
                <Badge size="sm" variant="light" color="blue">
                  {generationProgress.current}/{generationProgress.total}
                </Badge>
              )}
              {generationResults.length > 0 && (
                <>
                  <Badge size="sm" color="green" variant="light">
                    {generationResults.filter(r => r.status === 'success').length} ok
                  </Badge>
                  <Badge size="sm" color="red" variant="light">
                    {generationResults.filter(r => r.status === 'error').length} failed
                  </Badge>
                </>
              )}
            </Group>
            <Group gap="xs">
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconDownload size={14} />}
                onClick={handleExportResults}
                disabled={generationResults.length === 0}
              >
                Export
              </Button>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconRefresh size={14} />}
                onClick={() => {
                  setShowResults(false)
                  setGenerationProgress({ current: 0, total: 0 })
                  setLatestLeadSummary(null)
                }}
              >
                Close
              </Button>
            </Group>
          </Group>

          {generationResults.length === 0 && isGenerating && (
            <Text size="sm" c="dimmed">
              Generating…
            </Text>
          )}

          {generationResults.length > 0 && (
            <Stack gap="xs" style={{ maxHeight: 400, overflowY: 'auto' }}>
              {generationResults.map(result => (
                <Group key={result.id} justify="space-between" align="center" wrap="nowrap" gap="sm">
                  <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                    <Badge
                      size="sm"
                      color={result.status === 'success' ? 'green' : 'red'}
                      leftSection={result.status === 'success' ? <IconCheck size={10} /> : <IconX size={10} />}
                    >
                      {result.status}
                    </Badge>
                    <Text size="sm" truncate>
                      {result.template}
                    </Text>
                    {result.error && (
                      <Text size="xs" c="red" lineClamp={1} style={{ maxWidth: 200 }}>
                        {result.error}
                      </Text>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    {result.customerId
                      ? `ID: ${result.customerId}`
                      : result.data?.customer_id
                        ? `ProMax: ${result.data.customer_id}`
                        : '—'}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
        </Card>
      )}
    </Stack>
  )
}
